import type { CellContext } from "@msuite/ui/table";
import { Fragment } from "@msuite/ui/utils";
import type { FileBatchListItem } from "@msuite/utils/types";
import { FileRowActions } from "@/components/files";

/** Props Interface */
interface RowActionsProps extends CellContext<FileBatchListItem, "actions"> {
	/** Closes the report, for the one action that takes the reader off the page underneath it. */
	onNavigate: () => void;
}

/**
 * The report's row menu, which is the inbox's: a file the drop left behind is sorted here rather
 * than on a second page somebody has to be told about.
 *
 * **Only the two unsettled states carry it.** `POST /files/inbox/:fileId` refuses a classification
 * that is neither `unplaced` nor `failed`, so a filed row's menu would offer writes the server
 * answers 404 to, and filing is an insert rather than a move — a second destination for a file that
 * already has one is not what the menu would look like it was offering. A row still being read gets
 * nothing for a different reason: its candidates have not been written yet, so every entry the menu
 * exists for is missing, and the worker is about to file it anyway.
 */
export function RowActions({ cell, onNavigate }: RowActionsProps) {
	/** Computed */
	const item = cell.row.original;

	/** Guard */
	if (item.state !== "unplaced" && item.state !== "failed") return <Fragment />;

	/** Render */
	return (
		<FileRowActions
			file={item.file}
			candidates={item.candidates}
			documentKind={item.documentKind}
			onNavigate={onNavigate}
		/>
	);
}
